import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { LOGIN } from 'store/actions';
import { useNavigate } from 'react-router';
import { showSuccess } from 'utils/common';

const STORAGE_KEY = 'd1easytrans_auth';

function getAuthHeaders() {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return {};
  try {
    const auth = JSON.parse(raw);
    return auth.token ? { Authorization: 'Bearer ' + auth.token } : {};
  } catch (_) {
    return {};
  }
}

const useLogin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleAuthSuccess = (user) => {
    const authData = {
      token: user.access_token || user.token,
      oneapi_token: user.oneapi_token || user.access_token || user.token,
      username: user.username,
      id: user.user_id || user.id,
      role: user.role,
      quota: user.quota,
      d1_id: user.d1_id
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(authData));
    localStorage.setItem('user', JSON.stringify(user));
    dispatch({ type: LOGIN, payload: user });
    showSuccess('登录成功！');
    navigate('/panel');
  };

  // ── Password login ──
  const login = async (username, password) => {
    try {
      const fd = new FormData();
      fd.set('username', username);
      fd.set('password', password);
      const res = await fetch('/d1hub/auth/login', { method: 'POST', body: fd });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        return { success: false, message: j.detail || '登录失败' };
      }
      const user = await res.json();
      handleAuthSuccess(user);
      return { success: true, message: '' };
    } catch (err) {
      return { success: false, message: err.message || '网络错误' };
    }
  };

  // ── WeChat login ──
  const wechatLogin = async (code, displayName) => {
    try {
      const fd = new FormData();
      fd.set('code', code);
      fd.set('display_name', displayName || '');
      const res = await fetch('/d1hub/auth/wechat-login', { method: 'POST', body: fd });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        return { success: false, message: j.detail || '微信登录失败' };
      }
      const user = await res.json();
      handleAuthSuccess(user);
      if (user.is_new) {
        showSuccess('欢迎！D1Hub 账户已自动创建，赠送 100 D1积分。');
      }
      return { success: true, message: '' };
    } catch (err) {
      return { success: false, message: err.message || '网络错误' };
    }
  };

  // ── SMS login ──
  const smsLogin = async (phone, code, displayName) => {
    try {
      const fd = new FormData();
      fd.set('phone', phone);
      fd.set('code', code);
      fd.set('display_name', displayName || '');
      const res = await fetch('/d1hub/auth/sms-login', { method: 'POST', body: fd });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        return { success: false, message: j.detail || '验证失败' };
      }
      const user = await res.json();
      handleAuthSuccess(user);
      if (user.is_new) {
        showSuccess('欢迎！D1Hub 账户已自动创建，赠送 100 D1积分。');
      }
      return { success: true, message: '' };
    } catch (err) {
      return { success: false, message: err.message || '网络错误' };
    }
  };

  // ── Send SMS code ──
  const smsSend = async (phone, captchaToken, captchaAnswer) => {
    try {
      const fd = new FormData();
      fd.set('phone', phone);
      fd.set('captcha_token', captchaToken);
      fd.set('captcha_answer', captchaAnswer);
      const res = await fetch('/d1hub/auth/sms-send', { method: 'POST', body: fd });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        return { success: false, message: j.detail || '发送失败' };
      }
      return { success: true, message: '验证码已发送' };
    } catch (err) {
      return { success: false, message: err.message || '网络错误' };
    }
  };

  // ── Get captcha ──
  const getCaptcha = useCallback(async () => {
    try {
      const res = await fetch('/d1hub/auth/captcha');
      if (!res.ok) return null;
      return await res.json();
    } catch (_) {
      return null;
    }
  }, []);

  // ── Register ──
  const register = async (username, password, displayName) => {
    try {
      const fd = new FormData();
      fd.set('username', username);
      fd.set('password', password);
      fd.set('display_name', displayName || '');
      const res = await fetch('/d1hub/auth/register', { method: 'POST', body: fd });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        return { success: false, message: j.detail || '注册失败' };
      }
      const user = await res.json();
      handleAuthSuccess(user);
      showSuccess('注册成功！赠送 100 D1积分。');
      return { success: true, message: '' };
    } catch (err) {
      return { success: false, message: err.message || '网络错误' };
    }
  };

  // ── Get current user info ──
  const getMe = async () => {
    try {
      const res = await fetch('/d1hub/auth/me', { headers: getAuthHeaders() });
      if (!res.ok) return null;
      return await res.json();
    } catch (_) {
      return null;
    }
  };

  // ── Logout ──
  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('user');
    dispatch({ type: LOGIN, payload: null });
    navigate('/');
  };

  return { login, logout, wechatLogin, smsLogin, smsSend, getCaptcha, register, getMe, getAuthHeaders };
};

export default useLogin;
